import React, { useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Mail, Lock } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { apiService } from '@/lib/api';
import { useRecaptcha } from '@/hooks/useRecaptcha';
import { AuthShell, AuthLogo, PrimaryButton, OutlineButton, GoogleIcon, AUTH_SUBTLE, AUTH_LABEL, AUTH_BLUE } from './authShared';

const ICON = '#98A2B3';

function Login({ onLogin }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const { executeRecaptcha, isConfigured } = useRecaptcha();
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [isForgotPasswordOpen, setIsForgotPasswordOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [isSendingReset, setIsSendingReset] = useState(false);
  const [showVerificationNeeded, setShowVerificationNeeded] = useState(false);
  const [verificationEmail, setVerificationEmail] = useState('');
  const [isResending, setIsResending] = useState(false);
  // 2FA (email OTP)
  const [otpRequired, setOtpRequired] = useState(false);
  const [otpEmail, setOtpEmail] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const [isResendingOtp, setIsResendingOtp] = useState(false);

  const completeLogin = (userData) => {
    toast.success('Welcome back!');
    localStorage.setItem('user', JSON.stringify(userData));
    onLogin(userData);
    const pendingInviteToken = localStorage.getItem('pendingInviteToken');
    if (pendingInviteToken) {
      localStorage.removeItem('pendingInviteToken');
      localStorage.setItem('autoAcceptInvite', 'true');
      navigate(`/accept-invite?token=${pendingInviteToken}`);
    } else if (userData.is_admin) {
      navigate('/admin');
    } else {
      navigate('/dashboard');
    }
  };

  const handleVerifyOtp = async (e) => {
    if (e) e.preventDefault();
    if (otpCode.trim().length !== 6) {
      toast.error('Enter the 6-digit code from your email.');
      return;
    }
    setIsVerifyingOtp(true);
    try {
      const response = await apiService.verifyOtp(otpEmail, otpCode.trim());
      completeLogin(response.data);
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(detail || 'Invalid code. Please try again.');
      if (error.response?.status === 429 || error.response?.status === 400) {
        setOtpCode('');
      }
    } finally {
      setIsVerifyingOtp(false);
    }
  };

  const handleResendOtp = async () => {
    setIsResendingOtp(true);
    try {
      await apiService.resendOtp(otpEmail);
      toast.success('A new code is on its way to your email.');
      setOtpCode('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not resend the code. Try again shortly.');
    } finally {
      setIsResendingOtp(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotEmail) {
      toast.error('Please enter your email');
      return;
    }
    setIsSendingReset(true);
    try {
      await apiService.forgotPassword(forgotEmail);
      toast.success('If an account exists with this email, you will receive a password reset link.');
      setIsForgotPasswordOpen(false);
      setForgotEmail('');
    } catch (error) {
      toast.error('Failed to send reset email. Please try again.');
    } finally {
      setIsSendingReset(false);
    }
  };

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const pendingInviteToken = localStorage.getItem('pendingInviteToken');
    const redirectUrl = window.location.origin + '/dashboard';
    const authUrl = pendingInviteToken
      ? `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}&prompt=select_account`
      : `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    window.location.href = authUrl;
  };

  const handleResendVerification = async () => {
    if (!verificationEmail) return;
    setIsResending(true);
    try {
      await apiService.resendVerification(verificationEmail);
      toast.success('Verification email sent! Please check your inbox.');
    } catch (error) {
      toast.error('Failed to resend verification email. Please try again.');
    } finally {
      setIsResending(false);
    }
  };

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      let recaptchaToken = null;
      if (isConfigured) {
        try {
          recaptchaToken = await executeRecaptcha('login');
        } catch (err) {
          console.warn('reCAPTCHA failed:', err);
          toast.error('Security verification failed. Please refresh the page and try again.');
          setIsLoading(false);
          return;
        }
      }
      if (isConfigured && !recaptchaToken) {
        toast.error('Security verification is loading. Please wait a moment and try again.');
        setIsLoading(false);
        return;
      }
      const response = await apiService.login({
        email: formData.email,
        password: formData.password,
        recaptcha_token: recaptchaToken || ''
      });
      if (response.data?.otp_required) {
        setOtpEmail(response.data.email || formData.email);
        setOtpCode('');
        setOtpRequired(true);
        toast.success('We emailed you a 6-digit verification code.');
        return;
      }
      completeLogin(response.data);
    } catch (error) {
      console.error('Login error:', error.response?.data || error.message);
      if (error.response?.status === 403) {
        setVerificationEmail(formData.email);
        setShowVerificationNeeded(true);
        toast.error('Please verify your email first');
      } else if (error.response?.status === 401) {
        toast.error('Invalid email or password');
      } else if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else if (error.message === 'Network Error') {
        toast.error('Cannot connect to server. Please try again.');
      } else {
        toast.error('An error occurred. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // 2FA — enter the 6-digit code emailed after a correct password
  if (otpRequired) {
    return (
      <AuthShell backTo={null} showLegal>
        <div className="text-center mb-6">
          <div className="w-12 h-12 rounded-md flex items-center justify-center mx-auto mb-4" style={{ background: '#EEF2FB' }}>
            <Lock className="w-6 h-6" style={{ color: AUTH_BLUE }} />
          </div>
          <h1 className="text-2xl">Verify it&apos;s you</h1>
          <p className="mt-2 text-[15px]" style={{ color: AUTH_SUBTLE }}>
            Enter the 6-digit code we sent to<br />
            <span className="font-semibold" style={{ color: '#1D2939' }}>{otpEmail}</span>
          </p>
        </div>
        <form onSubmit={handleVerifyOtp} className="space-y-4">
          <Input
            data-testid="otp-code-input"
            inputMode="numeric"
            autoFocus
            maxLength={6}
            placeholder="000000"
            value={otpCode}
            onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            className="text-center text-2xl tracking-[0.5em] h-14"
            required
          />
          <PrimaryButton type="submit" data-testid="otp-verify-button" disabled={isVerifyingOtp}>
            {isVerifyingOtp ? 'Verifying...' : 'Verify & Sign In'}
          </PrimaryButton>
        </form>
        <div className="text-center mt-6 space-y-3">
          <p className="text-sm" style={{ color: AUTH_SUBTLE }}>Didn&apos;t get the code? Check spam, or</p>
          <button data-testid="otp-resend-button" onClick={handleResendOtp} disabled={isResendingOtp} className="text-sm font-semibold hover:opacity-70" style={{ color: AUTH_BLUE }}>
            {isResendingOtp ? 'Sending...' : 'Resend Code'}
          </button>
        </div>
        <div className="pt-4 mt-4 border-t text-center" style={{ borderColor: '#EAECF0' }}>
          <button data-testid="otp-back-button" onClick={() => { setOtpRequired(false); setOtpCode(''); }} className="text-sm hover:opacity-70" style={{ color: AUTH_SUBTLE }}>
            Back to Login
          </button>
        </div>
      </AuthShell>
    );
  }

  if (showVerificationNeeded) {
    return (
      <AuthShell backTo={null} showLegal>
        <div className="text-center mb-6">
          <div className="w-12 h-12 rounded-md flex items-center justify-center mx-auto mb-4" style={{ background: '#FEF3E2' }}>
            <Mail className="w-6 h-6" style={{ color: '#B45309' }} />
          </div>
          <h1 className="text-2xl">Email verification required</h1>
          <p className="mt-2 text-[15px]" style={{ color: AUTH_SUBTLE }}>Please verify your email before logging in</p>
        </div>
        <div className="rounded-md border p-4 text-center mb-4" style={{ borderColor: '#EAECF0', background: '#F8FAFC' }}>
          <p className="font-semibold" style={{ color: '#1D2939' }}>{verificationEmail}</p>
        </div>
        <p className="text-sm mb-6" style={{ color: AUTH_SUBTLE }}>
          We sent a verification link to your email when you signed up. Please check your inbox (and spam folder) and click the link to verify your account.
        </p>
        <OutlineButton onClick={handleResendVerification} disabled={isResending}>
          {isResending ? 'Sending...' : 'Resend Verification Email'}
        </OutlineButton>
        <div className="pt-4 mt-4 border-t text-center" style={{ borderColor: '#EAECF0' }}>
          <button onClick={() => setShowVerificationNeeded(false)} className="text-sm hover:opacity-70" style={{ color: AUTH_SUBTLE }}>Back to Login</button>
        </div>
      </AuthShell>
    );
  }

  return (
    <>
      <AuthShell showLegal>
        <div className="text-center mb-6">
          <AuthLogo className="mx-auto mb-4" />
          <h1 className="text-2xl">Welcome back</h1>
          <p className="mt-2 text-[15px]" style={{ color: AUTH_SUBTLE }}>Sign in to your DrawReady account</p>
        </div>

        {searchParams.get('expired') === '1' && (
          <div className="rounded-md border px-4 py-3 text-sm mb-4" style={{ background: '#FEF3E2', borderColor: '#FDE4B5', color: '#92400E' }} data-testid="session-expired-banner">
            You were signed out after 30 minutes of inactivity. Please sign in again.
          </div>
        )}

        <OutlineButton type="button" onClick={handleGoogleLogin}>
          <GoogleIcon /> Continue with Google
        </OutlineButton>

        <div className="flex items-center gap-3 my-5">
          <div className="flex-1 h-px" style={{ background: '#EAECF0' }} />
          <span className="text-sm" style={{ color: AUTH_SUBTLE }}>or sign in with email</span>
          <div className="flex-1 h-px" style={{ background: '#EAECF0' }} />
        </div>

        <form onSubmit={handleEmailLogin} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email" style={{ color: AUTH_LABEL }}>Email</Label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5" style={{ color: ICON }} />
              <Input id="email" type="email" placeholder="you@example.com" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} className="pl-10 h-12" required />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="password" style={{ color: AUTH_LABEL }}>Password</Label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5" style={{ color: ICON }} />
              <Input id="password" type="password" placeholder="••••••••" value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} className="pl-10 h-12" required />
            </div>
            <button type="button" onClick={() => { setForgotEmail(formData.email); setIsForgotPasswordOpen(true); }} className="text-sm font-medium hover:opacity-70 mt-1" style={{ color: AUTH_BLUE }}>
              Forgot password?
            </button>
          </div>
          <PrimaryButton type="submit" disabled={isLoading}>{isLoading ? 'Signing in...' : 'Sign In'}</PrimaryButton>
        </form>

        <p className="text-center text-sm mt-6" style={{ color: AUTH_SUBTLE }}>
          Don&apos;t have an account?{' '}
          <Link to="/signup" className="font-semibold hover:opacity-70" style={{ color: AUTH_BLUE }}>Sign up</Link>
        </p>
      </AuthShell>

      <Dialog open={isForgotPasswordOpen} onOpenChange={setIsForgotPasswordOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Reset Password</DialogTitle>
            <DialogDescription>Enter your email address and we&apos;ll send you a link to reset your password.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleForgotPassword} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="forgot-email">Email</Label>
              <Input id="forgot-email" type="email" placeholder="you@example.com" value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} required />
            </div>
            <div className="flex gap-3">
              <Button type="button" variant="outline" className="flex-1" onClick={() => setIsForgotPasswordOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={isSendingReset} className="flex-1">{isSendingReset ? 'Sending...' : 'Send Reset Link'}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default Login;
